import { useState, useRef, useEffect, useId, useCallback } from "react";
import { Search, X, Store } from "lucide-react";
import { useDebounce } from "src/libs/hooks";
import useStore from "src/hooks/useStore/useStore";

interface StoreSearchResult {
	id: number;
	name: string;
	region?: number;
	area?: number;
}

interface StoreSearchProps {
	onSelect: (store: StoreSearchResult) => void;
	resetKey?: number;
}

const StoreSearch = ({ onSelect, resetKey }: StoreSearchProps) => {
	const [inputValue, setInputValue] = useState("");
	const [debouncedValue, setDebouncedValue] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	const [selectedName, setSelectedName] = useState<string | null>(null);
	const [activeIndex, setActiveIndex] = useState(-1);
	const containerRef = useRef<HTMLDivElement>(null);
	const listboxId = useId();

	useDebounce(() => setDebouncedValue(inputValue), 300, [inputValue]);

	useEffect(() => {
		setInputValue("");
		setSelectedName(null);
		setDebouncedValue("");
		setActiveIndex(-1);
		setIsOpen(false);
	}, [resetKey]);

	const { data, isFetching } = useStore(debouncedValue, {
		enabled: debouncedValue.length >= 2
	});

	const results = data?.pages.flatMap(({ results }) => results) ?? [];

	const isLoading = isFetching && debouncedValue.length >= 2;
	const isPopupOpen = isOpen && debouncedValue.length >= 2;
	const showResults = isPopupOpen && results.length > 0;
	const showEmpty = isPopupOpen && results.length === 0 && !isLoading;

	useEffect(() => {
		if (debouncedValue.length >= 2 && !selectedName) {
			setIsOpen(true);
		} else {
			setIsOpen(false);
		}
		setActiveIndex(-1);
	}, [debouncedValue, results.length, selectedName]);

	useEffect(() => {
		const handleOutsideClick = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, []);

	const handleSelect = useCallback(
		(store: StoreSearchResult) => {
			setSelectedName(store.name);
			setInputValue(store.name);
			setIsOpen(false);
			setActiveIndex(-1);
			onSelect(store);
		},
		[onSelect]
	);

	const handleClear = () => {
		setInputValue("");
		setSelectedName(null);
		setDebouncedValue("");
		setActiveIndex(-1);
	};

	const handleInputChange = (value: string) => {
		setInputValue(value);
		if (selectedName) {
			setSelectedName(null);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Escape" && isPopupOpen) {
			e.preventDefault();
			setIsOpen(false);
			setActiveIndex(-1);
			return;
		}

		if (!showResults) return;

		switch (e.key) {
			case "ArrowDown":
				e.preventDefault();
				setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
				break;
			case "ArrowUp":
				e.preventDefault();
				setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
				break;
			case "Enter":
				e.preventDefault();
				if (activeIndex >= 0 && activeIndex < results.length) {
					handleSelect(results[activeIndex]);
				}
				break;
		}
	};

	const activeOptionId =
		activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined;

	return (
		<div ref={containerRef} className="relative">
			<div className="relative">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
				<input
					type="text"
					placeholder="Search by store name..."
					value={inputValue}
					onChange={(e) => handleInputChange(e.target.value)}
					onFocus={() => {
						if (debouncedValue.length >= 2 && !selectedName) {
							setIsOpen(true);
						}
					}}
					onKeyDown={handleKeyDown}
					role="combobox"
					aria-expanded={isPopupOpen}
					aria-haspopup="listbox"
					aria-autocomplete="list"
					aria-controls={listboxId}
					aria-activedescendant={activeOptionId}
					aria-label="Search by store name"
					className="w-full bg-white border border-border rounded-lg pl-9 pr-10 py-2.5 text-base font-medium focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
				/>
				{(inputValue || isLoading) && (
					<span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
						{isLoading ? (
							<span
								className="w-4 h-4 border-2 border-border border-t-primary rounded-full animate-spin block"
								role="status"
								aria-label="Searching..."
							/>
						) : (
							<button
								type="button"
								onClick={handleClear}
								className="p-0.5 rounded-full hover:bg-muted transition-colors"
								aria-label="Clear search"
							>
								<X className="h-4 w-4 text-muted-foreground" />
							</button>
						)}
					</span>
				)}
			</div>

			{showResults && (
				<div
					id={listboxId}
					role="listbox"
					className="absolute z-10 mt-1 bg-white border border-border rounded-xl shadow-md max-h-72 overflow-y-auto w-full"
					aria-label="Store search results"
				>
					{results.map((store, index) => (
						<button
							key={store.id}
							id={`${listboxId}-option-${index}`}
							type="button"
							role="option"
							aria-selected={index === activeIndex}
							tabIndex={-1}
							className={`w-full text-left px-3 py-2.5 transition-colors first:rounded-t-xl last:rounded-b-xl focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none ${index === activeIndex ? "bg-primary-lighter" : "hover:bg-primary-lighter"}`}
							onClick={() => handleSelect(store)}
						>
							<div className="flex items-center gap-3">
								<div
									className="flex-shrink-0 w-9 h-9 rounded-full bg-foreground text-white flex items-center justify-center overflow-hidden"
									aria-hidden="true"
								>
									<Store className="h-4 w-4" />
								</div>
								<span className="font-medium text-sm text-foreground truncate">
									{store.name}
								</span>
							</div>
						</button>
					))}
				</div>
			)}

			{showEmpty && (
				<div
					role="status"
					className="absolute z-10 mt-1 bg-white border border-border rounded-xl shadow-md w-full px-3 py-3 text-sm text-muted-foreground"
				>
					No stores found
				</div>
			)}
		</div>
	);
};

export default StoreSearch;
